import { z } from 'zod'
import { api } from './baseApi'
import { OrderSchema } from './orders.api'
import { PaymentSummaryData, PaymentSuccessResponse } from '@/components/blocks/ChargeMember/types'

// Location types (simplified to match backend response)
const LocationSchema = z.object({
  merchantId: z.string(),
  locationId: z.string(),
  name: z.string(),
  currency: z.string(),
  businessSchedule: z.object({
    schedule: z.record(z.object({
      timeRanges: z.array(z.any()),
    })),
  }).nullable(),
  phone: z.string(),
  posType: z.string(),
  status: z.string(),
  address: z.object({
    city: z.string().nullable().optional(),
    street: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    latitude: z.number(),
    longitude: z.number(),
    createdOn: z.string(),
    updatedOn: z.string(),
  }).nullable(),
  imageUrl: z.string().nullable(),
  createdOn: z.string(),
  updatedOn: z.string(),
  reservationUrl: z.string(),
  menuUrl: z.string(),
})

// Member types
const MemberSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string(),
  isVerified: z.boolean(),
  isBlocked: z.boolean(),
  dateOfBirth: z.string().nullable(),
  imageUrl: z.string().nullable(),
  currency: z.string(),
  hasPaymentMethod: z.boolean().nullable(),
})

// Response types
export const MemberDetailsResponseSchema = z.object({
  location: LocationSchema,
  member: MemberSchema,
  orders: z.array(OrderSchema),
  totalVisits: z.number(),
  lifetimeSpend: z.number(),
  lifetimeTip: z.number(),
  lastVisitDate: z.string().nullable(),
  currency: z.string(),
})

const MembersResponseSchema = z.object({
  items: z.array(MemberSchema),
  hasMore: z.boolean().optional(),
  lastId: z.string().optional(),
})

export type MemberDetailsResponse = z.infer<typeof MemberDetailsResponseSchema>
export type Member = z.infer<typeof MemberSchema>

// Request types
export interface MemberRequestParams {
  memberId: string
}

interface MembersResponse {
  items: Member[]
  hasMore: boolean
  lastId?: string
}

interface MembersRequestParams {
  pageSize?: number
  startAfterId?: string
  filters: Record<string, string>
  sortBy: string | null
  sortDirection: 'asc' | 'desc'
}

export interface PaymentBreakdownRequest {
  memberId: string;
  locationId: string;
  amountToPay: number;
  tipAmount: number;
  note?: string;
}

export interface PaymentBreakdownResponse {
  totalAmount: number;
}

export interface ApiErrorResponse {
  reasons: string[];
  message: string;
  metadata: Record<string, unknown>;
}

export interface OtpResponse {
  otpPaymentId: string;
}

// Members API implementation
export const membersApi = {
  /**
   * Get a single member by ID
   */
  getMember: async (params: MemberRequestParams): Promise<MemberDetailsResponse> => {
    const response = await api.get<MemberDetailsResponse>(`/merchant/members/${params.memberId}`)
    return MemberDetailsResponseSchema.parse(response.data)
  },

  fetchMembers: async (params: MembersRequestParams): Promise<MembersResponse> => {
    const response = await api.get('/merchant/members', { 
      params: {
        pageSize: params.pageSize,
        startAfterId: params.startAfterId,
        ...params.filters,
        sortBy: params.sortBy || undefined,
        sortDirection: params.sortDirection,
      }
    })

    const data = MembersResponseSchema.parse(response.data)

    return {
      items: data.items,
      hasMore: data.hasMore ?? false,
      lastId: data.lastId
    }
  },

  getPaymentBreakdown: async (params: {
    memberId: string;
    locationId: string;
    amountToPay: number;
    tipAmount: number;
    note?: string;
  }): Promise<PaymentSummaryData> => {
    const response = await api.post<PaymentSummaryData>(
      "/merchant/payments/non-integrated/payment-breakdown",
      params
    );
    return response.data;
  },

  requestOtp: async (params: {
    memberId: string;
    locationId: string;
    amountToPay: number;
    tipAmount: number;
    note?: string;
  }): Promise<OtpResponse> => {
    const response = await api.post<OtpResponse>(
      "/merchant/payments/non-integrated/request-otp", 
      params
    );
    return response.data;
  },

  verifyOtp: async (params: {
    otpPaymentId: string;
    otpCode: string;
    locationId: string;
  }): Promise<PaymentSuccessResponse> => {
    const response = await api.post<PaymentSuccessResponse>(
      "/merchant/payments/non-integrated/verify-otp",
      params
    );
    return response.data;
  },
} 